import cx from 'classnames'
import { useMemo } from 'react'

import Icon from '@/components/Icon'
import { useT } from '@/utils/language'

import styles from './SubscriptionCampaign.module.css'

type ClassNameProps = {
  className?: string
}

function formatCampaignText(template: string, values: Record<string, string>) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(value),
    template
  )
}

function useCampaignValues() {
  const T = useT()

  return {
    endDate: T('subscriptionPlanCampaignEndDateLabel'),
    freeMonths: T('subscriptionPlanCampaignFreeMonths'),
    price: T('subscriptionPlanPriceValue'),
  }
}

export function useSubscriptionCampaignNotes() {
  const T = useT()
  const { endDate, freeMonths, price } = useCampaignValues()

  return useMemo(
    () =>
      [
        T('subscriptionPlanCampaignNote1'),
        T('subscriptionPlanCampaignNote2'),
        T('subscriptionPlanCampaignNote3'),
        T('subscriptionPlanCampaignNote4'),
        T('subscriptionPlanCampaignNote5'),
        T('subscriptionPlanCampaignNote6'),
        T('subscriptionPlanCampaignNote7'),
      ].map((note) => formatCampaignText(note, { endDate, freeMonths, price })),
    [T, endDate, freeMonths, price]
  )
}

export function SubscriptionCampaignLimitedBadge(props: ClassNameProps) {
  const { className } = props
  const T = useT()

  return (
    <div className={cx(styles.limitedBadge, className)}>
      <div className={styles.limitedLabel}>
        <Icon className={styles.limitedIcon} name="accessTime" size={13} />
        <span>{T('subscriptionPlanCampaignLimited')}</span>
      </div>
      <div className={styles.limitedDate}>
        {T('subscriptionPlanCampaignEndDateLabel')}
        {T('until')}
      </div>
    </div>
  )
}

type CheckRowProps = {
  children: React.ReactNode
  className?: string
  textClassName?: string
}

export function SubscriptionCampaignCheckRow(props: CheckRowProps) {
  const { children, className, textClassName } = props

  return (
    <div className={cx(styles.checkRow, className)}>
      <span className={styles.checkCircle}>
        <Icon className={styles.checkIcon} name="check" size={14} />
      </span>
      <span className={cx(styles.checkText, textClassName)}>{children}</span>
    </div>
  )
}

export function SubscriptionCampaignInlinePrice(props: ClassNameProps) {
  const { className } = props
  const T = useT()
  const { freeMonths, price } = useCampaignValues()

  return (
    <div className={cx(styles.inlinePrice, className)}>
      <span className={styles.inlinePricePrefix}>
        {T('subscriptionPlanPriceHint')}{' '}
      </span>
      <span className={styles.inlinePriceStrike}>{price}</span>
      <span className={styles.inlinePriceArrow}>→</span>
      <span className={styles.inlinePriceFreeMonths}>{freeMonths}</span>
      <span className={styles.inlinePriceZero}>0</span>
      <span className={styles.inlinePriceYen}>{T('jpy')}</span>
    </div>
  )
}

export function SubscriptionCampaignPriceCard(props: ClassNameProps) {
  const { className } = props
  const T = useT()
  const { freeMonths, price } = useCampaignValues()

  return (
    <div className={className}>
      <div className={styles.priceCard}>
        <div className={styles.priceCardHint}>
          {T('subscriptionPlanPriceHint')}
        </div>
        <div className={styles.priceCardValue}>
          <div className={styles.priceCardLine}>
            <span className={styles.priceCardStrike}>{price}</span>
            <span className={styles.priceCardFreeMonths}>→{freeMonths}</span>
            <span className={styles.priceCardZero}>0</span>
            <span className={styles.priceCardYen}>{T('jpy')}</span>
          </div>
          <div className={styles.priceCardTax}>{T('withTax')}</div>
        </div>
      </div>
      <div className={styles.firstUseOnly}>
        {T('subscriptionPlanCampaignFirstUseOnly')}
      </div>
    </div>
  )
}
